import React, { useRef, useState } from 'react';
import { Icon } from 'semantic-ui-react';
import ActiveFilterChips from './ActiveFilterChips';
import AdvancedFiltersPopover from './AdvancedFiltersPopover';
import FilterFieldRenderer from './FilterFieldRenderer';
import { useFilterState } from './hooks/useFilterState';
import {
  ActionsGroup,
  BadgeCount,
  ChildrenSlot,
  FieldGroup,
  FieldLabel,
  FilterBarRow,
  FilterRoot,
  IconButton,
  MoreFiltersButton,
  RelativeWrap
} from './styles';
import { FilterBarProps, FilterFieldContext } from './types';

const FilterBar: React.FC<FilterBarProps> = ({
  fields,
  initialValues,
  onSearch,
  onClear,
  loading,
  syncToUrl = true,
  urlNamespace,
  hasExternalActiveFilters = false,
  actions,
  children
}) => {
  const advancedAnchorRef = useRef<HTMLButtonElement>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const {
    draftValues,
    appliedValues,
    setValue,
    applySearch,
    clearAll,
    clearAdvanced,
    removeAppliedField,
    advancedActiveCount,
    hasAnyActive,
    activeAdvancedFields
  } = useFilterState({
    fields,
    initialValues,
    syncToUrl,
    urlNamespace,
    onSearch,
    onClear
  });

  const visibleFields = fields.filter(field => !field.hidden);
  const mainFields = visibleFields.filter(field => !field.advanced);
  const advancedFields = visibleFields.filter(field => field.advanced);

  const ctx: FilterFieldContext = {
    values: draftValues,
    setValue,
    loading
  };

  return (
    <FilterRoot>
      <FilterBarRow>
        {mainFields.map(field => {
          if (field.kind === 'custom') {
            const customContent = field.render(ctx);
            if (customContent == null || customContent === false) {
              return null;
            }
            return (
              <FieldGroup
                key={field.name}
                flex={field.width || 1}
                minWidth={140}
              >
                {customContent}
              </FieldGroup>
            );
          }

          return (
            <FieldGroup
              key={field.name}
              flex={field.width || (field.kind === 'search' ? 1.4 : 1)}
              minWidth={field.kind === 'search' ? 220 : 140}
            >
              {field.kind !== 'checkbox' && (
                <FieldLabel htmlFor={`filter-${field.name}`}>{field.label}</FieldLabel>
              )}
              <FilterFieldRenderer
                field={field}
                ctx={ctx}
                onSearch={() => applySearch()}
              />
            </FieldGroup>
          );
        })}

        <ActionsGroup>
          {actions}

          {advancedFields.length > 0 && (
            <RelativeWrap style={{ width: 'auto' }}>
              <MoreFiltersButton
                ref={advancedAnchorRef}
                type="button"
                active={advancedOpen}
                aria-expanded={advancedOpen}
                aria-haspopup="dialog"
                onClick={() => setAdvancedOpen(prev => !prev)}
              >
                <Icon name="sliders horizontal" />
                Mais filtros
                {advancedActiveCount > 0 && (
                  <BadgeCount>{advancedActiveCount}</BadgeCount>
                )}
              </MoreFiltersButton>

              <AdvancedFiltersPopover
                open={advancedOpen}
                onClose={() => setAdvancedOpen(false)}
                anchorRef={advancedAnchorRef}
                fields={advancedFields}
                values={draftValues}
                setValue={setValue}
                onClear={clearAdvanced}
                onApply={() => applySearch()}
                loading={loading}
              />
            </RelativeWrap>
          )}

          <IconButton
            type="button"
            primary
            aria-label="Pesquisar"
            disabled={loading}
            onClick={() => applySearch()}
          >
            <Icon name="search" />
          </IconButton>

          <IconButton
            type="button"
            aria-label="Limpar filtros"
            disabled={loading || (!hasAnyActive && !hasExternalActiveFilters)}
            onClick={clearAll}
          >
            <Icon name="close" />
          </IconButton>
        </ActionsGroup>
      </FilterBarRow>

      <ActiveFilterChips
        fields={activeAdvancedFields}
        values={appliedValues}
        onRemove={removeAppliedField}
      />

      {children && <ChildrenSlot>{children}</ChildrenSlot>}
    </FilterRoot>
  );
};

export type { FilterBarProps, FilterField, FilterValues, SearchType } from './types';
export default FilterBar;
